import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Download, Upload, KeyRound, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/admin/AdminGate";
import { SectionHeading } from "@/components/SectionHeading";
import {
  getAdminQuestions,
  importQuestions,
  patchQuestion,
  downloadCsv,
} from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const Container = ({ children }) => (
  <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">{children}</div>
);

const FilterSelect = ({ value, onChange, options, label }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    aria-label={label}
    className="h-10 rounded-lg border border-[color:rgba(10,27,51,0.2)] bg-white px-3 text-sm"
  >
    <option value="">{label}</option>
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

function QuestionBank() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ portion: "", difficulty: "", status: "" });
  const [importText, setImportText] = useState("");

  const cleanParams = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["admin-questions", filters],
    queryFn: () => getAdminQuestions(cleanParams),
  });

  const toggleActive = async (q) => {
    const next = q.active_status === "active" ? "inactive" : "active";
    await patchQuestion(q.id, { active_status: next });
    qc.invalidateQueries({ queryKey: ["admin-questions"] });
  };

  const toggleTrap = async (q) => {
    await patchQuestion(q.id, { trap_question: !q.trap_question });
    qc.invalidateQueries({ queryKey: ["admin-questions"] });
  };

  const doImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
      if (!Array.isArray(parsed)) throw new Error("not array");
    } catch (e) {
      toast.error("Invalid JSON", { description: "Paste a JSON array of questions." });
      return;
    }
    try {
      const res = await importQuestions(parsed);
      toast.success(`Imported ${res.imported} question(s)`);
      setImportText("");
      qc.invalidateQueries({ queryKey: ["admin-questions"] });
    } catch (e) {
      toast.error("Import failed");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect label="All portions" value={filters.portion} onChange={(v) => setFilters((f) => ({ ...f, portion: v }))} options={["state", "national", "math"]} />
        <FilterSelect label="All difficulty" value={filters.difficulty} onChange={(v) => setFilters((f) => ({ ...f, difficulty: v }))} options={["easy", "medium", "hard"]} />
        <FilterSelect label="All status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={["active", "inactive"]} />
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={() => downloadCsv("/admin/questions/export", "questions.csv")} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:rgba(10,27,51,0.2)] px-3 py-2 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]">
            <Download className="h-4 w-4" /> Questions CSV
          </button>
          <button onClick={() => downloadCsv("/admin/leads/export", "diagnostic_leads.csv")} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:rgba(10,27,51,0.2)] px-3 py-2 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]">
            <Download className="h-4 w-4" /> Leads CSV
          </button>
          <button onClick={() => downloadCsv("/admin/performance/export", "user_performance.csv")} className="inline-flex items-center gap-1.5 rounded-lg border border-[color:rgba(10,27,51,0.2)] px-3 py-2 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]">
            <Download className="h-4 w-4" /> Performance CSV
          </button>
        </div>
      </div>

      <p className="mt-4 text-sm font-medium text-[color:rgba(11,18,32,0.6)]">
        {isLoading ? "Loading..." : `${questions.length} question(s)`}
      </p>

      <div className="mt-3 overflow-x-auto rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-[var(--ibp-offwhite)] text-xs uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
            <tr>
              <th className="px-4 py-3">ID</th>
              <th className="px-4 py-3">Portion</th>
              <th className="px-4 py-3">Topic</th>
              <th className="px-4 py-3">Diff</th>
              <th className="px-4 py-3">Question</th>
              <th className="px-4 py-3">Trap</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {questions.map((q) => (
              <tr key={q.id} data-testid={STUDY.adminQuestionRow} className="border-t border-[color:rgba(10,27,51,0.08)]">
                <td className="px-4 py-3 font-mono text-xs">{q.id}</td>
                <td className="px-4 py-3 capitalize">{q.portion}</td>
                <td className="px-4 py-3">{q.outline_section || q.topic}</td>
                <td className="px-4 py-3 capitalize">{q.difficulty}</td>
                <td className="max-w-md px-4 py-3 text-[color:rgba(11,18,32,0.8)]">{q.question_text}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleTrap(q)}
                    data-testid="admin-question-trap-toggle"
                    title="Toggle Trap Drill (EXCEPT/NOT/FALSE)"
                    className={[
                      "rounded-full px-2.5 py-1 text-xs font-semibold",
                      q.trap_question
                        ? "bg-[color:rgba(214,162,30,0.18)] text-[#9a6b00]"
                        : "bg-[color:rgba(11,18,32,0.08)] text-[color:rgba(11,18,32,0.55)]",
                    ].join(" ")}
                  >
                    {q.trap_question ? "Trap" : "—"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleActive(q)}
                    className={[
                      "rounded-full px-2.5 py-1 text-xs font-semibold",
                      q.active_status === "active"
                        ? "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]"
                        : "bg-[color:rgba(11,18,32,0.08)] text-[color:rgba(11,18,32,0.6)]",
                    ].join(" ")}
                  >
                    {q.active_status}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]">
          <Upload className="h-4 w-4" /> Import questions (JSON array)
        </h3>
        <textarea
          data-testid={STUDY.adminImport}
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          rows={5}
          placeholder='[{"id":"q-101","portion":"state","topic":"Agency","difficulty":"easy","question_text":"...","choice_a":"...","choice_b":"...","choice_c":"...","choice_d":"...","correct_answer":"A","explanation":"..."}]'
          className="mt-3 w-full rounded-xl border border-[color:rgba(10,27,51,0.2)] p-3 font-mono text-xs"
        />
        <button onClick={doImport} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">
          Import
        </button>
      </div>
    </div>
  );
}

function AccessCodesLink() {
  return (
    <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[color:rgba(212,160,23,0.15)] text-[var(--ibp-gold-600,#b8860b)]">
          <KeyRound className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-sm font-semibold text-[var(--ibp-ink)]">Student accounts</h3>
          <p className="text-sm text-[color:rgba(11,18,32,0.6)]">
            Review free and paid students, purchase access, progress, and recent sign-ins.
          </p>
        </div>
      </div>
      <Link
        to="/admin/students"
        className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
      >
        View student accounts <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export default function AdminQuestions() {
  return (
    <div data-testid={STUDY.adminQuestionsPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-14">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="Question bank"
            subtitle="Import, view, filter, activate, and export questions."
          />
        </Container>
      </section>
      <section className="py-12 sm:py-16">
        <Container>
          <AdminGate>
            <QuestionBank />
            <AccessCodesLink />
          </AdminGate>
        </Container>
      </section>
    </div>
  );
}
